import { useState, useCallback } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { t } from '@lingui/core/macro'

export const DataTable = ({ data, columns, itemContent, isLoading }) => {
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
  })
  const [sortConfig, setSortConfig] = useState({
    key: columns.find(c => c.defaultSortable)?.key || columns[0].key,
    direction: 'DESC',
  })

  const { page, perPage } = pagination
  const rowsPerPageOptions = [10, 20, 30]
  const totalPages = data && Math.ceil(data.length / perPage)
  const startIndex = (page - 1) * perPage

  const onChangePagination = useCallback((newPage = page, newPerPage = perPage) => {
    setPagination({ page: newPage, perPage: newPerPage })
  }, [])

  const requestSort = useCallback(
    key => {
      const direction = sortConfig.key === key && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC'

      setSortConfig({ key, direction })
    },
    [setSortConfig, sortConfig],
  )

  const sortedData =
    data &&
    [...data].sort((a, b) => {
      const column = columns.find(c => c.key === sortConfig.key)

      if (column?.type === 'date') {
        const dateA = new Date(a[sortConfig.key])
        const dateB = new Date(b[sortConfig.key])
        return sortConfig.direction === 'ASC'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime()
      }

      if (column?.type === 'number') {
        const numA = Number(a[sortConfig.key] || 0)
        const numB = Number(b[sortConfig.key] || 0)
        return sortConfig.direction === 'ASC' ? numA - numB : numB - numA
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ASC' ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ASC' ? 1 : -1
      }
      return 0
    })

  const displayedData = sortedData?.slice(startIndex, startIndex + perPage)

  return (
    <table className="table-fixed w-full">
      <thead>
        <Heading>
          {columns.map(({ label, key, sortable, columnWidth }) => (
            <Column
              label={label}
              sortKey={key}
              sortable={sortable}
              requestSort={requestSort}
              sortConfig={sortConfig}
              columnWidth={columnWidth}
              key={key}
            />
          ))}
        </Heading>
      </thead>
      <tbody>
        {isLoading ? (
          <EmptyPlaceholder />
        ) : displayedData ? (
          displayedData.map((item, index) => itemContent(index, item))
        ) : null}
      </tbody>
      <tfoot>
        <Footer
          pagination={pagination}
          onChangePagination={onChangePagination}
          rowsPerPageOptions={rowsPerPageOptions}
          startIndex={startIndex}
          data={data}
          totalPages={totalPages}
        />
      </tfoot>
    </table>
  )
}

const Heading = ({ children }) => {
  return <tr className="border border-cv-blue-200 bg-cv-blue-100">{children}</tr>
}

const Column = ({ label, sortKey, sortable, requestSort, sortConfig, columnWidth }) => {
  return (
    <th
      onClick={() => (sortable ? requestSort(sortKey) : null)}
      className="p-4 first:pl-[20px] text-left uppercase text-[11px]"
      style={{ width: columnWidth }}
    >
      <span className={`text-cv-blue-700 leading-[18px] ${sortable ? 'cursor-pointer' : ''}`}>
        {label}{' '}
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'ASC' ? (
            <ArrowUp size={18} className="inline-block mt-[-2px]" />
          ) : (
            <ArrowDown size={18} className="inline-block mt-[-2px]" />
          )
        ) : (
          ''
        )}
      </span>
    </th>
  )
}

const EmptyPlaceholder = () =>
  Array.from({ length: 10 }).map((_, index) => (
    <tr key={index} className="border border-cv-blue-200 bg-white animate-pulse">
      <td className="py-4 px-2 text-left">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]" />
      </td>
      <td className="p-4 min-w-[360px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
      <td className="py-4 pl-2 min-w-[140px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
      <td className="py-4 pl-2 min-w-[140px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
      <td className="py-4 pl-2 min-w-[140px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
    </tr>
  ))

const Footer = ({
  pagination,
  onChangePagination,
  rowsPerPageOptions,
  startIndex,
  data,
  totalPages,
}) => {
  return (
    <tr className="border border-cv-blue-200 border-t-cv-blue-200 bg-cv-blue-100 text-cv-blue-800 text-[10px]">
      <td colSpan={5} className="py-4 text-right pr-6">
        <span className="mr-8">{t`Rows per page:`}</span>
        <span className="mr-8">
          <select
            className="bg-cv-blue-100"
            value={pagination.perPage}
            onChange={e => {
              onChangePagination(1, parseInt(e.target.value))
            }}
          >
            {rowsPerPageOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </span>
        {data ? (
          <span className="mr-8">
            {startIndex + 1}-{Math.min(startIndex + pagination.perPage, data?.length)} {t`of`}{' '}
            {data?.length} {t`Rows`}
          </span>
        ) : null}
        <span>
          <button
            className="mr-8 text-black disabled:text-[#ADBABF]"
            disabled={pagination.page === 1}
            onClick={() => onChangePagination(Math.max(pagination.page - 1, 1), pagination.perPage)}
          >
            <ChevronLeft size={18} className="inline-block" />
          </button>
          <button
            className="text-black disabled:text-[#ADBABF]"
            disabled={pagination.page === totalPages}
            onClick={() =>
              onChangePagination(Math.min(pagination.page + 1, totalPages), pagination.perPage)
            }
          >
            <ChevronRight size={18} className="inline-block" />
          </button>
        </span>
      </td>
    </tr>
  )
}
